export const LIVER_FORM_STATUSES = ['active', 'inactive', 'graduated'] as const

export type LiverFormStatus = (typeof LIVER_FORM_STATUSES)[number]

export type LiverFormValues = {
  display_name: string
  line_user_id?: string | null
  avatar_url?: string | null
  status: LiverFormStatus
  joined_at?: string | null
  left_at?: string | null
  notes?: string | null
}

export type LiverPayload = {
  display_name: string
  line_user_id: string | null
  avatar_url: string | null
  status: LiverFormStatus
  joined_at: string | null
  left_at: string | null
  notes: string | null
}

export type LiverFormValidationResult =
  | { valid: true }
  | { valid: false; error: string }

export type LiverFormFieldName = keyof LiverFormValues
export type LiverFormFieldRequirement = 'required' | 'optional'

export type LiverFormFieldMeta = {
  label: string
  requirement: LiverFormFieldRequirement
  requirementLabel: string
}

const FIELD_META: Record<LiverFormFieldName, LiverFormFieldMeta> = {
  display_name: { label: 'ライバー名', requirement: 'required', requirementLabel: '必須' },
  line_user_id: { label: 'LINE ID', requirement: 'optional', requirementLabel: '任意' },
  avatar_url: { label: 'アイコン画像URL', requirement: 'optional', requirementLabel: '任意' },
  status: { label: 'ステータス', requirement: 'required', requirementLabel: '必須' },
  joined_at: { label: '入所日', requirement: 'optional', requirementLabel: '任意' },
  left_at: { label: '退所日', requirement: 'optional', requirementLabel: '任意' },
  notes: { label: 'メモ', requirement: 'optional', requirementLabel: '任意' }
}

export function getLiverFormFieldMeta(field: LiverFormFieldName): LiverFormFieldMeta {
  return FIELD_META[field]
}

export function buildLiverPayload(values: LiverFormValues): LiverPayload {
  return {
    display_name: trimRequired(values.display_name),
    line_user_id: trimOptional(values.line_user_id),
    avatar_url: trimOptional(values.avatar_url),
    status: values.status,
    joined_at: trimOptional(values.joined_at),
    left_at: trimOptional(values.left_at),
    notes: trimOptional(values.notes)
  }
}

export function validateLiverForm(values: LiverFormValues): LiverFormValidationResult {
  if (!values.display_name.trim()) {
    return { valid: false, error: 'ライバー名を入力してください' }
  }

  if (!LIVER_FORM_STATUSES.includes(values.status)) {
    return { valid: false, error: 'ステータスを選択してください' }
  }

  const joinedAt = trimOptional(values.joined_at)
  if (joinedAt && !isDateOnly(joinedAt)) {
    return { valid: false, error: '入所日は YYYY-MM-DD 形式で入力してください' }
  }

  const leftAt = trimOptional(values.left_at)
  if (leftAt && !isDateOnly(leftAt)) {
    return { valid: false, error: '退所日は YYYY-MM-DD 形式で入力してください' }
  }

  return { valid: true }
}

function trimRequired(value: string): string {
  return value.trim()
}

function trimOptional(value: string | null | undefined): string | null {
  const text = value?.trim() ?? ''
  return text.length > 0 ? text : null
}

function isDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
}
